import { useQuery } from '@tanstack/react-query'
import { ArrowLeft, BarChart3 } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { Navigate, useNavigate } from 'react-router-dom'
import { freeTrialApi } from '@/api/freeTrials'
import { useLcTrialProgram } from '@/hooks/admin/useLcTrialProgram'

export default function TrialAnalyticsPage() {
  const { t } = useTranslation(['admin', 'common'])
  const navigate = useNavigate()
  const programQ = useLcTrialProgram()
  const analyticsQ = useQuery({
    queryKey: ['lcFreeTrialAnalytics'],
    queryFn: async () => (await freeTrialApi.analytics()).data.data,
    enabled: programQ.data?.enabled === true,
  })
  const program = programQ.data
  const analytics = analyticsQ.data
  // 회차 단위 집계(spec trial-end §9.2) — 같은 원생이 여러 회차로 등장할 수 있다
  const cards = analytics ? [
    ['granted', analytics.grantedAttemptCount],
    ['consumed', analytics.consumedAttemptCount],
    ['unusedRevoked', analytics.unusedRevokedAttemptCount],
    ['started', analytics.startedCount],
    ['active', analytics.activeCount],
    ['converted', analytics.convertedCount],
    ['expired', analytics.expiredCount],
    ['revoked', analytics.revokedCount],
  ] as const : []

  if (programQ.isLoading) {
    return <div className="p-4 md:p-6">{t('common:status.loading')}</div>
  }

  if (programQ.isError) {
    return <p className="m-4 rounded-lg bg-red-50 p-4 text-sm text-red-700 md:m-6">{t('admin:trialAnalytics.loadError')}</p>
  }

  if (!program?.enabled) {
    return <Navigate to="/admin/classes" replace />
  }

  return (
    <div className="space-y-6 p-4 md:p-6">
      <header className="flex items-center gap-3">
        <button type="button" onClick={() => navigate('/admin/classes')} className="rounded-lg p-2 hover:bg-gray-100">
          <ArrowLeft className="h-5 w-5" />
        </button>
        <BarChart3 className="h-6 w-6 text-primary-600" />
        <div>
          <h1 className="text-xl font-bold text-gray-900">{t('admin:trialAnalytics.title')}</h1>
          <p className="text-sm text-gray-500">{t('admin:trialAnalytics.description')}</p>
        </div>
      </header>

      {analyticsQ.isLoading && <p>{t('common:status.loading')}</p>}
      {analyticsQ.isError && (
        <p className="rounded-lg bg-red-50 p-4 text-sm text-red-700">{t('admin:trialAnalytics.loadError')}</p>
      )}

      {program && (
        <section className="rounded-xl border border-gray-200 bg-white p-5">
          <h2 className="font-semibold text-gray-900">{t('admin:trialAnalytics.program')}</h2>
          <div className="mt-3 grid gap-3 text-sm sm:grid-cols-4">
            <Metric label={t('admin:trialAnalytics.firstGranted')} value={program.firstGrantedOn ?? '—'} />
            <Metric label={t('admin:trialAnalytics.closesOn')} value={program.closesOn ?? '—'} />
            <Metric label={t('admin:trialAnalytics.used')} value={`${program.consumedAttemptCount} / 80`} />
            <Metric
              label={t('admin:trialAnalytics.status')}
              value={t(
                !program.enabled
                  ? 'admin:trialAnalytics.disabled'
                  : program.open
                    ? 'admin:trialAnalytics.open'
                    : 'admin:trialAnalytics.closed',
              )}
            />
          </div>
        </section>
      )}

      {analytics && (
        <>
          <section className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
            {cards.map(([key, value]) => (
              <Metric key={key} label={t(`admin:trialAnalytics.cards.${key}`)} value={value} card />
            ))}
          </section>
          <section className="grid gap-4 lg:grid-cols-2">
            <div className="rounded-xl border border-gray-200 bg-white p-5">
              <h2 className="font-semibold text-gray-900">{t('admin:trialAnalytics.learning')}</h2>
              <div className="mt-4 grid grid-cols-2 gap-4">
                <Metric label={t('admin:trialAnalytics.lessonStarted')} value={analytics.lessonStartedCount} />
                <Metric label={t('admin:trialAnalytics.lessonCompleted')} value={analytics.lessonCompletedCount} />
                <Metric label={t('admin:trialAnalytics.componentStarted')} value={analytics.componentStartedCount} />
                <Metric label={t('admin:trialAnalytics.componentCompleted')} value={analytics.componentCompletedCount} />
              </div>
            </div>
            <div className="rounded-xl border border-gray-200 bg-white p-5">
              <h2 className="font-semibold text-gray-900">{t('admin:trialAnalytics.conversion')}</h2>
              <div className="mt-4 grid grid-cols-2 gap-4">
                <Metric label={t('admin:trialAnalytics.conversionRate')} value={`${analytics.conversionRatePercent.toFixed(1)}%`} />
                <Metric label={t('admin:trialAnalytics.averageHours')} value={analytics.averageHoursToPayment == null ? '—' : analytics.averageHoursToPayment.toFixed(1)} />
                <Metric label={t('admin:trialAnalytics.averageLessons')} value={analytics.averageCompletedLessonsInSevenDays.toFixed(1)} />
                <Metric label={t('admin:trialAnalytics.bySet')} value={Object.entries(analytics.byTrialSet).map(([set, count]) => `${set} ${count}`).join(' · ') || '—'} />
              </div>
            </div>
          </section>
        </>
      )}
    </div>
  )
}

function Metric({ label, value, card = false }: { label: string; value: string | number; card?: boolean }) {
  return (
    <div className={card ? 'rounded-xl border border-gray-200 bg-white p-4' : ''}>
      <p className="text-xs text-gray-500">{label}</p>
      <p className="mt-1 text-lg font-bold text-gray-900">{value}</p>
    </div>
  )
}
